import { useMemo } from 'react';
import * as z from 'zod';
import { useCountry } from '@/contexts/CountryContext';
import { ExpenseCategory } from '@/types/expense';

interface CountrySpecificValidationRules {
  // Amount validation
  minAmount: number;
  maxAmount: number;
  allowedCurrencies: string[];
  
  // Date validation
  maxDaysBack: number; // How far back expenses can be dated
  
  // Receipt requirements
  requireReceiptAboveAmount: number; // Amount above which receipt is required
  
  // Category restrictions
  allowedCategories: ExpenseCategory[];
  restrictedCategories: ExpenseCategory[];
  
  // File upload restrictions
  maxFileSize: number; // in bytes
  allowedFileTypes: string[];
  
  // Description requirements
  minDescriptionLength: number;
  maxDescriptionLength: number;
}

// Default validation rules (UK-based)
const DEFAULT_RULES: CountrySpecificValidationRules = {
  minAmount: 0.01,
  maxAmount: 10000,
  allowedCurrencies: ['GBP'],
  maxDaysBack: 365,
  requireReceiptAboveAmount: 25, // £25
  allowedCategories: Object.values(ExpenseCategory),
  restrictedCategories: [],
  maxFileSize: 10 * 1024 * 1024, // 10MB
  allowedFileTypes: ["image/jpeg", "image/png", "application/pdf"],
  minDescriptionLength: 1,
  maxDescriptionLength: 500,
};

// Country-specific rules
const COUNTRY_RULES: Record<string, Partial<CountrySpecificValidationRules>> = {
  'GB': {
    ...DEFAULT_RULES,
    // UK-specific rules
    maxAmount: 10000, // £10,000 limit
    requireReceiptAboveAmount: 25, // £25 receipt requirement
  },
  'US': {
    ...DEFAULT_RULES,
    // US-specific rules
    minAmount: 0.01,
    maxAmount: 15000, // $15,000 limit
    allowedCurrencies: ['USD'],
    requireReceiptAboveAmount: 75, // $75 receipt requirement
    maxDaysBack: 180, // IRS prefers expenses to be claimed within 180 days
    // US might have different category restrictions for tax purposes
    restrictedCategories: [], // Could add categories that aren't deductible in US
  },
  // Add more countries as needed
};

/**
 * Custom hook that provides country-specific expense validation rules and schema
 */
export const useExpenseValidation = () => {
  const { currentCountry } = useCountry();
  
  const validationRules = useMemo((): CountrySpecificValidationRules => {
    const countryCode = currentCountry?.code || 'GB';
    const countrySpecificRules = COUNTRY_RULES[countryCode] || {};
    
    return {
      ...DEFAULT_RULES,
      ...countrySpecificRules,
    };
  }, [currentCountry]);

  const createExpenseSchema = useMemo(() => {
    return z.object({
      contractorContractId: z.string().min(1, "Contract is required"),
      amount: z
        .string()
        .min(1, "Amount is required")
        .refine((val) => {
          const num = parseFloat(val);
          return !isNaN(num) && num >= validationRules.minAmount;
        }, `Amount must be at least ${validationRules.minAmount}`)
        .refine((val) => {
          const num = parseFloat(val);
          return !isNaN(num) && num <= validationRules.maxAmount;
        }, `Amount cannot exceed ${validationRules.maxAmount}`),
      description: z
        .string()
        .min(validationRules.minDescriptionLength, `Description must be at least ${validationRules.minDescriptionLength} character(s)`)
        .max(validationRules.maxDescriptionLength, `Description must be less than ${validationRules.maxDescriptionLength} characters`),
      expenseDate: z
        .string()
        .min(1, "Expense date is required")
        .refine((dateString) => {
          const date = new Date(dateString);
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          return date <= today;
        }, "Expense date cannot be in the future")
        .refine((dateString) => {
          const date = new Date(dateString);
          const oldestAllowed = new Date();
          oldestAllowed.setDate(oldestAllowed.getDate() - validationRules.maxDaysBack);
          return date >= oldestAllowed;
        }, `Expense date cannot be more than ${validationRules.maxDaysBack} days ago`),
      category: z.nativeEnum(ExpenseCategory, {
        required_error: "Category is required",
      }).refine((category) => {
        return validationRules.allowedCategories.includes(category) && 
               !validationRules.restrictedCategories.includes(category);
      }, "This expense category is not allowed in your country"),
      receipt: z
        .any()
        .optional()
        .refine((files) => {
          if (!files || files.length === 0) return true; // Optional
          const file = files[0];
          return validationRules.allowedFileTypes.includes(file.type);
        }, `File must be one of: ${validationRules.allowedFileTypes.join(', ')}`)
        .refine((files) => {
          if (!files || files.length === 0) return true; // Optional
          const file = files[0];
          return file.size <= validationRules.maxFileSize;
        }, `File size must be less than ${Math.round(validationRules.maxFileSize / (1024 * 1024))}MB`),
    }).superRefine((data, ctx) => {
      // Custom validation: Receipt required above certain amount
      const amount = parseFloat(data.amount);
      if (!isNaN(amount) && amount >= validationRules.requireReceiptAboveAmount) {
        if (!data.receipt || data.receipt.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['receipt'],
            message: `Receipt is required for expenses of ${validationRules.requireReceiptAboveAmount} or more`,
          });
        }
      }
    });
  }, [validationRules]);

  const getValidationMessage = (field: string, value?: any): string | null => {
    switch (field) {
      case 'amount':
        if (typeof value === 'string') {
          const num = parseFloat(value);
          if (!isNaN(num) && num >= validationRules.requireReceiptAboveAmount) {
            return `Receipt required for amounts ${validationRules.requireReceiptAboveAmount} and above`;
          }
        }
        return null;
      
      case 'expenseDate':
        return `Expenses can be dated up to ${validationRules.maxDaysBack} days ago`;
      
      default:
        return null;
    }
  };

  const isReceiptRequired = (amount: string | number): boolean => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return !isNaN(numAmount) && numAmount >= validationRules.requireReceiptAboveAmount;
  };

  const getMaxAmount = (): number => validationRules.maxAmount;
  
  const getMinAmount = (): number => validationRules.minAmount;

  const getAllowedCategories = (): ExpenseCategory[] => {
    return validationRules.allowedCategories.filter(
      category => !validationRules.restrictedCategories.includes(category)
    );
  };

  return {
    validationRules,
    createExpenseSchema,
    getValidationMessage,
    isReceiptRequired,
    getMaxAmount,
    getMinAmount,
    getAllowedCategories,
  };
};

export default useExpenseValidation; 